import { useEffect, useRef } from 'react'
import styles from './Help.module.css'

/**
 * The Help Centre's filter field.
 *
 * Sticky, because a long open section would otherwise push it off screen and
 * the reader would scroll back up to change one word. It carries no state of its
 * own: the page owns the query, so the same string drives the list, the count
 * and this field.
 */
export function HelpSearchField({
  query,
  onQuery,
  count,
  total,
}: {
  query: string
  onQuery: (value: string) => void
  count: number
  total: number
}) {
  const input = useRef<HTMLInputElement>(null)

  // "/" is the convention for jumping to a search field, and costs nothing to
  // someone who never learns it. It must not fire while the reader is typing
  // somewhere else, and must leave browser shortcuts alone.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const active = document.activeElement as HTMLElement | null
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return
      event.preventDefault()
      input.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className={styles.searchRow}>
      <div className={styles.searchBox}>
        <Glass />
        <label className="dp-sr-only" htmlFor="help-search">
          Search help
        </label>
        <input
          id="help-search"
          ref={input}
          className={styles.searchInput}
          type="search"
          value={query}
          placeholder="Search help"
          autoComplete="off"
          spellCheck={false}
          aria-controls="help-sections"
          aria-describedby="help-search-count"
          onChange={(event) => onQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return
            if (query) onQuery('')
            else input.current?.blur()
          }}
        />
        <div className={styles.searchHint}>
          {query ? (
            <button
              type="button"
              className={styles.searchClear}
              aria-label="Clear"
              onClick={() => {
                onQuery('')
                input.current?.focus()
              }}
            >
              <Cross />
            </button>
          ) : (
            <kbd className={styles.searchKey}>/</kbd>
          )}
        </div>
      </div>
      <p className={styles.searchCount} id="help-search-count" role="status" aria-live="polite">
        {query ? `${count} of ${total} sections` : ''}
      </p>
    </div>
  )
}

const Glass = () => (
  <svg className={styles.searchGlass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
)

const Cross = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
)
